import { useState, type ChangeEvent, type FormEvent } from 'react';
import emailjs from '@emailjs/browser';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { COLORS } from '../theme/colors';
import { GradientButton } from '../components/ui/SharedUI';
import PageLayout from '../components/layout/PageLayout';
import type { ContactFormLabels, ContactFormValues, ContactInfo, HeroContent } from '../types/content';

type FormErrors = Partial<Record<keyof ContactFormValues | 'contacto', string>>;

type ContactInfoCardProps = ContactInfo;

const INITIAL_FORM_VALUES: ContactFormValues = {
  nombre: '',
  empresa: '',
  correo: '',
  whatsapp: '',
  mensaje: '',
};

function isValidEmail(value: string): boolean {
  if (!value.trim()) return false;

  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

function normalizePhone(value: string): string {
  return value.replace(/[^\d+]/g, '');
}

function isValidWhatsApp(value: string): boolean {
  const normalized = normalizePhone(value);

  return /^\+?\d{10,15}$/.test(normalized);
}

function ContactInfoCard({ slogan, email, phone }: ContactInfoCardProps): JSX.Element {
  return (
    <Box
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: '22px',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(145deg, rgba(30,27,46,0.94) 0%, rgba(24,22,41,0.98) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 22px 70px rgba(0,0,0,0.22)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 18% 20%, rgba(168,85,247,0.14) 0%, transparent 38%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: '1.35rem', md: '1.55rem' },
            fontWeight: 700,
            lineHeight: 1.3,
            mb: 3.5,
            background: COLORS.gradients.brand,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {slogan}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.8 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              background: 'rgba(168,85,247,0.13)',
              border: '1px solid rgba(168,85,247,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <EmailIcon sx={{ color: COLORS.purple[400], fontSize: 21 }} />
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                fontSize: '0.8rem',
                mb: 0.3,
              }}
            >
              Correo electrónico
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                fontWeight: 700,
                fontSize: '0.95rem',
              }}
            >
              {email}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              background: 'rgba(168,85,247,0.13)',
              border: '1px solid rgba(168,85,247,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <WhatsAppIcon sx={{ color: COLORS.purple[400], fontSize: 21 }} />
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                fontSize: '0.8rem',
                mb: 0.3,
              }}
            >
              WhatsApp
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                fontWeight: 700,
                fontSize: '0.95rem',
              }}
            >
              {phone}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

type ContactFormProps = {
  form: ContactFormLabels;
};

function ContactForm({ form }: ContactFormProps): JSX.Element {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [values, setValues] = useState<ContactFormValues>(INITIAL_FORM_VALUES);
  const [errors, setErrors] = useState<FormErrors>({});
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [sending, setSending] = useState(false);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      color: 'text.primary',
      background: isDark ? 'rgba(255,255,255,0.045)' : 'rgba(255,255,255,0.72)',
      transition: 'all 0.22s ease',
      '& fieldset': {
        borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(147,51,234,0.18)',
      },
      '&:hover fieldset': {
        borderColor: COLORS.purple[500],
      },
      '&.Mui-focused fieldset': {
        borderColor: COLORS.purple[500],
        borderWidth: 1,
      },
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      fontSize: '0.95rem',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: COLORS.purple[400],
    },
    '& .MuiFormHelperText-root': {
      mx: 0,
      mt: 0.8,
    },
  };

  const validateForm = (): FormErrors => {
    const nextErrors: FormErrors = {};

    if (!values.nombre.trim()) {
      nextErrors.nombre = 'Escribe tu nombre completo.';
    }

    if (!values.mensaje.trim()) {
      nextErrors.mensaje = 'Escribe un mensaje.';
    }

    const hasEmail = values.correo.trim().length > 0;
    const hasWhatsApp = values.whatsapp.trim().length > 0;

    if (!hasEmail && !hasWhatsApp) {
      nextErrors.contacto = 'Escribe un correo electrónico válido o un WhatsApp válido.';
      nextErrors.correo = 'Correo o WhatsApp requerido.';
      nextErrors.whatsapp = 'Correo o WhatsApp requerido.';
      return nextErrors;
    }

    if (hasEmail && !isValidEmail(values.correo)) {
      nextErrors.correo = 'Escribe un correo electrónico válido.';
    }

    if (hasWhatsApp && !isValidWhatsApp(values.whatsapp)) {
      nextErrors.whatsapp = 'Escribe un WhatsApp válido con 10 a 15 dígitos.';
    }

    if (!hasEmail && hasWhatsApp && !isValidWhatsApp(values.whatsapp)) {
      nextErrors.contacto = 'El WhatsApp no tiene un formato válido.';
    }

    if (!hasWhatsApp && hasEmail && !isValidEmail(values.correo)) {
      nextErrors.contacto = 'El correo electrónico no tiene un formato válido.';
    }

    return nextErrors;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = event.target;

    setValues((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
      contacto: undefined,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const nextErrors = validateForm();

    if (Object.values(nextErrors).some(Boolean)) {
      setErrors(nextErrors);
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setSubmitError(true);
      return;
    }

    try {
      setSending(true);
      setSubmitError(false);

      await emailjs.send(
        serviceId,
        templateId,
        {
          name: values.nombre.trim(),
          from_name: values.nombre.trim(),
          empresa: values.empresa.trim(),
          company: values.empresa.trim(),
          email: values.correo.trim(),
          from_email: values.correo.trim(),
          reply_to: values.correo.trim(),
          whatsapp: values.whatsapp.trim(),
          phone: values.whatsapp.trim(),
          message: values.mensaje.trim(),
          mensaje: values.mensaje.trim(),
        },
        {
          publicKey,
        },
      );

      setSuccess(true);
      setValues(INITIAL_FORM_VALUES);
      setErrors({});
    } catch {
      setSubmitError(true);
    } finally {
      setSending(false);
    }
  };

  const handleSuccessClose = (): void => {
    setSuccess(false);
  };

  const handleErrorClose = (): void => {
    setSubmitError(false);
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: '22px',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(145deg, rgba(30,27,46,0.94) 0%, rgba(24,22,41,0.98) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 22px 70px rgba(0,0,0,0.22)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 86% 12%, rgba(168,85,247,0.12) 0%, transparent 38%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Grid container spacing={2.5} sx={{ position: 'relative', zIndex: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label={form.nombre}
            name="nombre"
            value={values.nombre}
            onChange={handleChange}
            placeholder={form.placeholders.nombre}
            error={Boolean(errors.nombre)}
            helperText={errors.nombre}
            sx={fieldSx}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={form.empresa}
            name="empresa"
            value={values.empresa}
            onChange={handleChange}
            placeholder={form.placeholders.empresa}
            sx={fieldSx}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={form.correo}
            name="correo"
            type="email"
            value={values.correo}
            onChange={handleChange}
            placeholder={form.placeholders.correo}
            error={Boolean(errors.correo)}
            helperText={errors.correo}
            sx={fieldSx}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={form.whatsapp}
            name="whatsapp"
            value={values.whatsapp}
            onChange={handleChange}
            placeholder={form.placeholders.whatsapp}
            error={Boolean(errors.whatsapp)}
            helperText={errors.whatsapp}
            sx={fieldSx}
          />
        </Grid>

        {errors.contacto && (
          <Grid item xs={12}>
            <Alert
              severity="warning"
              sx={{
                borderRadius: '14px',
                background: isDark ? 'rgba(251,191,36,0.1)' : 'rgba(251,191,36,0.16)',
                color: 'text.primary',
                border: '1px solid rgba(251,191,36,0.22)',
              }}
            >
              {errors.contacto}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label={form.mensaje}
            name="mensaje"
            multiline
            minRows={5}
            value={values.mensaje}
            onChange={handleChange}
            placeholder={form.placeholders.mensaje}
            error={Boolean(errors.mensaje)}
            helperText={errors.mensaje}
            sx={fieldSx}
          />
        </Grid>

        <Grid item xs={12}>
          <GradientButton
            fullWidth
            type="submit"
            disabled={sending}
            sx={{
              py: 1.6,
              fontSize: '0.98rem',
            }}
          >
            {sending ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.2 }}>
                <CircularProgress size={18} sx={{ color: '#fff' }} />
                Enviando...
              </Box>
            ) : (
              form.submit
            )}
          </GradientButton>
        </Grid>
      </Grid>

      <Snackbar
        open={success}
        autoHideDuration={4200}
        onClose={handleSuccessClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={handleSuccessClose} sx={{ borderRadius: '12px' }}>
          {form.success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={submitError}
        autoHideDuration={5200}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={handleErrorClose} sx={{ borderRadius: '12px' }}>
          No se pudo enviar el mensaje. Revisa la configuración de EmailJS o intenta nuevamente.
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default function ContactoPage(): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const hero = t('contacto.hero', { returnObjects: true }) as Required<HeroContent>;
  const info = t('contacto.info', { returnObjects: true }) as ContactInfo;
  const form = t('contacto.form', { returnObjects: true }) as ContactFormLabels;

  return (
    <PageLayout>
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          background: isDark
            ? `
              radial-gradient(circle at 50% 4%, rgba(168,85,247,0.18) 0%, rgba(147,51,234,0.08) 28%, transparent 56%),
              radial-gradient(circle at 12% 44%, rgba(168,85,247,0.10) 0%, transparent 36%),
              radial-gradient(circle at 86% 54%, rgba(216,180,254,0.08) 0%, transparent 34%),
              linear-gradient(180deg, ${COLORS.dark[900]} 0%, ${COLORS.dark[800]} 48%, ${COLORS.dark[900]} 100%)
            `
            : `
              radial-gradient(circle at 50% 4%, rgba(168,85,247,0.14) 0%, rgba(216,180,254,0.10) 28%, transparent 56%),
              linear-gradient(180deg, ${COLORS.light[100]} 0%, ${COLORS.gray[100]} 100%)
            `,
          pt: { xs: 10, md: 13 },
          pb: { xs: 10, md: 13 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: -180, md: -260 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 580, md: 980 },
            height: { xs: 580, md: 980 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(168,85,247,0.18) 0%, rgba(147,51,234,0.08) 34%, transparent 70%)',
            filter: 'blur(70px)',
            opacity: isDark ? 0.7 : 0.34,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 5, md: 6 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box sx={{ maxWidth: 520 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: {
                      xs: '2rem',
                      sm: '2.45rem',
                      md: '3.15rem',
                      lg: '3.45rem',
                    },
                    lineHeight: {
                      xs: 1.16,
                      md: 1.12,
                    },
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 2.5,
                  }}
                >
                  {hero.title1}{' '}
                  <Box
                    component="span"
                    sx={{
                      display: { xs: 'inline', md: 'block' },
                      background: COLORS.gradients.brand,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {hero.highlight}
                  </Box>
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    maxWidth: 460,
                    fontSize: { xs: '1rem', md: '1.15rem' },
                    lineHeight: 1.7,
                    mb: 4.5,
                  }}
                >
                  {hero.subtext}
                </Typography>

                <ContactInfoCard slogan={info.slogan} email={info.email} phone={info.phone} />
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <ContactForm form={form} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </PageLayout>
  );
}